import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';

/**
 * Pull `data.root.props.dataSources` out of a Puck-shaped DSL `data` payload.
 *
 * Per child SAD ADR-10 the canonical declaration site is
 * `root.props.dataSources` — there is no second source-of-truth at
 * `dslConfig.dataSources`. Anything else (missing root, missing props,
 * non-object dataSources block) returns an empty record.
 *
 * Defensive parsing: malformed individual entries (non-object, null) are
 * dropped with a single `console.warn` per offender and the remaining
 * entries pass through unchanged. Never throws — the bridge must keep
 * rendering even when one declaration is bad.
 */
export function extractDataSources(
  data: unknown,
): Record<string, DataSourceDeclaration> {
  if (!isPlainObject(data)) {
    return {};
  }
  const root = (data as { root?: unknown }).root;
  if (!isPlainObject(root)) {
    return {};
  }
  const props = (root as { props?: unknown }).props;
  if (!isPlainObject(props)) {
    return {};
  }
  const raw = (props as { dataSources?: unknown }).dataSources;
  if (!isPlainObject(raw)) {
    return {};
  }

  const out: Record<string, DataSourceDeclaration> = {};
  for (const [name, decl] of Object.entries(raw)) {
    if (!isPlainObject(decl)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[ConfigurableStep] dataSources["${name}"] is not an object; skipping.`,
      );
      continue;
    }
    out[name] = decl as unknown as DataSourceDeclaration;
  }
  return out;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
